import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import {
  RingostatCallJournalItem,
  RingostatCallJournalResponse
} from 'src/typings';
import { RingostatCallService } from '../ringostat-call.service';

@Component({
  selector: 'app-ringostat-call-journal',
  templateUrl: './call-journal.component.html',
  styleUrls: ['./call-journal.component.scss']
})
export class CallJournalComponent implements OnInit {
  readonly displayedColumns = ['call', 'client', 'manager', 'status', 'recording', 'actions'];

  readonly dayOptions = [
    { value: 1, label: 'Сегодня' },
    { value: 7, label: '7 дней' },
    { value: 30, label: '30 дней' },
    { value: 90, label: '90 дней' }
  ];

  response: RingostatCallJournalResponse | null = null;

  calls: RingostatCallJournalItem[] = [];

  loading = false;

  error: string | null = null;

  selectedDays = 7;

  missedOnly = true;

  callingCallId: string | null = null;

  hidingCallId: string | null = null;

  constructor(
    private readonly ringostatCallService: RingostatCallService,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.loading = true;
    this.error = null;

    this.ringostatCallService
      .getCallJournal({
        days: this.selectedDays,
        limit: 300,
        missedOnly: this.missedOnly
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          this.response = response;
          this.calls = response.items || [];
        },
        error: () => {
          this.response = null;
          this.calls = [];
          this.error = 'Не удалось загрузить журнал звонков.';
        }
      });
  }

  trackByCall(_: number, call: RingostatCallJournalItem) {
    return call.id;
  }

  get totalLabel() {
    const response = this.response;
    if (!response) return '0';
    return this.missedOnly
      ? `${response.unreturnedMissedTotal + response.voicemailTotal} требуют внимания`
      : `${response.total} звонков`;
  }

  get periodLabel() {
    const response = this.response;
    if (!response) return '';
    return `${this.formatPeriodDate(response.from)} - ${this.formatPeriodDate(response.to)}`;
  }

  getClientLink(call: RingostatCallJournalItem) {
    return call.clientId ? ['/client/profile', call.clientId] : null;
  }

  getClientLabel(call: RingostatCallJournalItem) {
    return call.clientName || call.clientPhone || 'Клиент не найден';
  }

  getClientMeta(call: RingostatCallJournalItem) {
    if (call.clientName && call.clientPhone) return call.clientPhone;
    return call.clientId ? `Клиент #${call.clientId}` : 'Нет карточки';
  }

  getStatusClass(call: RingostatCallJournalItem) {
    if (call.isVoicemail) return 'statusChip statusChip--voicemail';
    return call.missed ? 'statusChip statusChip--missed' : 'statusChip statusChip--answered';
  }

  getDirectionClass(call: RingostatCallJournalItem) {
    return call.direction === 'to_client' ? 'directionChip directionChip--out' : 'directionChip';
  }

  getCallbackPhone(call: RingostatCallJournalItem) {
    return call.clientPhone || call.normalizedCaller || call.normalizedDestination || null;
  }

  canClickToCall(call: RingostatCallJournalItem) {
    return Boolean(this.getCallbackPhone(call));
  }

  async clickToCall(call: RingostatCallJournalItem) {
    const phone = this.getCallbackPhone(call);
    if (!phone || this.callingCallId) return;

    this.callingCallId = call.id;
    try {
      await firstValueFrom(
        this.ringostatCallService.clickToCall({
          phone,
          clientId: call.clientId || undefined
        })
      );
      this.notificationService.notice('Звонок клиенту запущен: после ответа клиента примите вызов');
    } catch (error: unknown) {
      this.notificationService.showError(
        extractApiErrorMessage(error, 'Не удалось запустить звонок')
      );
    } finally {
      this.callingCallId = null;
    }
  }

  async hideCall(call: RingostatCallJournalItem) {
    if (this.hidingCallId) return;
    const confirmed = globalThis.confirm(
      'Скрыть этот звонок из журнала и убрать его из счётчика внимания?'
    );
    if (!confirmed) return;

    this.hidingCallId = call.id;
    try {
      await firstValueFrom(this.ringostatCallService.hideCall(call.id));
      this.calls = this.calls.filter((item) => item.id !== call.id);
      this.load();
      this.notificationService.notice('Звонок скрыт');
    } catch (error: unknown) {
      this.notificationService.showError(
        extractApiErrorMessage(error, 'Не удалось скрыть звонок')
      );
    } finally {
      this.hidingCallId = null;
    }
  }

  getManagerLabel(call: RingostatCallJournalItem) {
    return call.managerLabel || call.managerUserName || this.formatLineLabel(call.managerNumber) || '-';
  }

  private formatLineLabel(value?: string | null) {
    const raw = String(value || '').trim();
    if (!raw) return null;

    const angleMatch = raw.match(/<([^>]+)>/);
    const normalized = (angleMatch?.[1] || raw).replace(/\D/g, '');
    const local = normalized.startsWith('38') ? normalized.slice(2) : normalized;
    if (/^0\d{9}$/.test(local)) {
      return `(${local.slice(0, 3)}) ${local.slice(3, 6)} ${local.slice(6, 8)} ${local.slice(8)}`;
    }

    return 'Линия не найдена';
  }

  private formatPeriodDate(value: string) {
    const date = new Date(String(value || '').replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return value || '-';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }
}
